<style scoped>
.settings-card {
	transition:
		transform 0.2s,
		box-shadow 0.2s;
	height: 100%;
}
.settings-card:hover {
	transform: translateY(-5px);
	box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}
.settings-icon {
	font-size: 2.5rem;
	width: 80px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	margin-bottom: 1.5rem;
}
.icon-users {
	color: #6f42c1;
	background-color: rgba(111, 66, 193, 0.1);
}
.icon-species {
	color: #0dcaf0;
	background-color: rgba(13, 202, 240, 0.1);
}
.icon-integrations {
	color: #0d6efd;
	background-color: rgba(13, 110, 253, 0.1);
}
.icon-bell {
	color: #20c997;
	background-color: rgba(32, 201, 151, 0.1);
}
.icon-notifications {
	color: #fd7e14;
	background-color: rgba(253, 126, 20, 0.1);
}
.icon-general {
	color: #6c757d;
	background-color: rgba(108, 117, 125, 0.1);
}
.icon-preferences {
	color: #6610f2;
	background-color: rgba(102, 16, 242, 0.1);
}
.icon-equipment {
	color: #dc3545;
	background-color: rgba(220, 53, 69, 0.1);
}
.icon-tracking {
	color: #198754;
	background-color: rgba(25, 135, 84, 0.1);
}
.last-updated {
	font-size: 0.8rem;
	color: #6c757d;
}
</style>
<template>
	<div class="container py-5">
		<div class="row mb-4">
			<div class="col">
				<h1 class="display-5 mb-3">Settings</h1>
				<p class="text-muted lead">
					Configure your organization's preferences and integrations
				</p>
			</div>
		</div>

		<div class="row g-4">
			<!-- Preferences Card. First because it is the one card on this page
			     that is not the district's: it holds the reader's own settings,
			     so every member opens it, and the copy says so rather than
			     letting it read as another org-admin screen. -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-preferences">
							<i class="bi bi-sliders2"></i>
						</div>
						<h2 class="h4 mb-2">Preferences</h2>
						<p class="text-muted mb-4">
							Your own settings, such as how the district's tags are drawn for
							you on the map. These affect you alone.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/preferences">
								<button class="btn btn-outline-primary">
									Open Preferences
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- User Management Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-users">
							<i class="bi bi-people-fill"></i>
						</div>
						<h2 class="h4 mb-2">User Management</h2>
						<p class="text-muted mb-4">
							Manage staff accounts, roles, and permissions for your
							organization.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/user">
								<button class="btn btn-outline-primary">
									Manage Users
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Species Card: the shared species catalog and the district's
			     local set adopted from it. -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-species">
							<i class="bi bi-bug"></i>
						</div>
						<h2 class="h4 mb-2">Species</h2>
						<p class="text-muted mb-4">
							The shared species catalog, and your district's own set adopted
							from it.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/species">
								<button class="btn btn-outline-info">
									Manage Species
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Integrations Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-integrations">
							<i class="bi bi-gear-wide-connected"></i>
						</div>
						<h2 class="h4 mb-2">Integrations</h2>
						<p class="text-muted mb-4">
							Configure connections with FieldSeeker, VectorSurv, and other
							services.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/integration">
								<button class="btn btn-outline-primary">
									Manage Integrations
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Organization Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-equipment">
							<i class="bi bi-globe-europe-africa"></i>
						</div>
						<h2 class="h4 mb-2">Organization</h2>
						<p class="text-muted mb-4">
							Manage your organization service area and information, the Site
							tag vocabulary, and the symbol library the map draws tags with.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/organization">
								<button class="btn btn-outline-danger">
									Manage Organization
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Notifications Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-bell">
							<i class="bi bi-bell-fill"></i>
						</div>
						<h2 class="h4 mb-2">Notifications</h2>
						<p class="text-muted mb-4">
							Choose which channels notify you by email or SMS.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/notifications">
								<button class="btn btn-outline-success">
									Manage Notifications
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Link Manager Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-integrations">
							<i class="bi bi-link-45deg"></i>
						</div>
						<h2 class="h4 mb-2">Link Manager</h2>
						<p class="text-muted mb-4">
							Create short links for mosquito services and track where residents
							click through from.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/links">
								<button class="btn btn-outline-primary">
									Manage Links
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Import Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-notifications">
							<i class="bi bi-upload"></i>
						</div>
						<h2 class="h4 mb-2">Uploads</h2>
						<p class="text-muted mb-4">
							Upload files (spreadsheets, scans, notes) to make the data
							available to Nidus
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/upload">
								<button class="btn btn-outline-warning">
									Manage Uploads
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- Tracking Devices Card: phones reporting their position through the
			     Traccar app, so the district keeps a location trace independent of
			     notes. Placed after Uploads because both concern evidence arriving
			     from outside Nidus. -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-tracking">
							<i class="bi bi-geo-alt-fill"></i>
						</div>
						<h2 class="h4 mb-2">Tracking Devices</h2>
						<p class="text-muted mb-4">
							Phones reporting their position through the Traccar app, so the
							district keeps a location trace independent of notes.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<RouterLink to="/_/configuration/tracking-devices">
								<button class="btn btn-outline-primary">
									Manage Devices
									<i class="bi bi-arrow-right ms-1"></i>
								</button>
							</RouterLink>
						</div>
					</div>
				</div>
			</div>

			<!-- General Settings Card -->
			<div class="col-12 col-md-6 col-lg-4">
				<div class="card settings-card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="settings-icon icon-general">
							<i class="bi bi-sliders"></i>
						</div>
						<h2 class="h4 mb-2">General Settings</h2>
						<p class="text-muted mb-4">
							Configure organization details, branding, and system preferences.
						</p>
						<div class="d-flex justify-content-between align-items-center">
							<a class="btn btn-outline-secondary disabled" disabled>
								Manage Settings
								<i class="bi bi-arrow-right ms-1"></i>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mt-5 text-center text-muted">
			<p class="small">
				<i class="bi bi-shield-lock me-1"></i>
				All changes made in settings are logged for audit purposes
			</p>
		</div>
	</div>
</template>
